<script>
import {
    metadataState
} from '@app/store/metadataStore.svelte.js';
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    v4 as uuidv4
} from 'uuid';
import { onMount } from 'svelte';
import {
	fade
} from 'svelte/transition';
import * as m from "@app/paraglide/messages.js";

let metadataType = $state("");
let metadataValue = $state("");
let isAdding = $state(false);
let typeFocus;

onMount(() => {
    if (typeFocus) {
        typeFocus.focus();
    }
});

function addMetadata() {
    if (!metadataType || !metadataValue) return;
    
    // Use push instead of spread assignment
    metadataState.push({
        id: uuidv4(),
        type: metadataType,
        value: metadataValue,
    });

    // Record history
    insertHistoryAction({
        name: "insert metadata",
        eventGroup: projectState.selected,
        snapshots: [{
            store: "metadataState",
            value: JSON.stringify(metadataState)
        }]
    });

    // Clear inputs
    metadataType = "";
    metadataValue = "";
    
    // Auto-focus back to the type field
    if (typeFocus) {
        setTimeout(() => typeFocus.focus(), 0);
    }
}

function removeMetadata(id) {
    // Find index and use splice instead of filter assignment
    const index = metadataState.findIndex(metadata => metadata.id === id);
    if (index !== -1) {
        metadataState.splice(index, 1);
    }

    insertHistoryAction({
        name: "remove metadata",
        eventGroup: projectState.selected,
        snapshots: [{
            store: "metadataState",
            value: JSON.stringify(metadataState)
        }]
    });
}

// Handle pressing Enter in the input fields
function handleKeydown(event) {
    if (event.key === 'Enter' && metadataType && metadataValue) {
        addMetadata();
    }
}
</script>

<div class="metadata-container">
    <div class="add-metadata-card">
        <div class="add-metadata-header">
            <h5>{m.options_metadata_add_title()}</h5>
            <p class="text-muted mb-3">{m.options_metadata_add_desc()}</p>
        </div>
        <div class="add-metadata-form">
            <div class="input-group mb-2">
                <input 
                    type="text" 
                    class="form-control" 
                    placeholder={m.options_metadata_type_placeholder()} 
                    bind:value={metadataType}
                    bind:this={typeFocus}
                    onkeydown={handleKeydown}
                    id="metadataTypeInput"
                    aria-label={m.options_metadata_type_label()}
                >
                
                <input 
                    type="text" 
                    class="form-control" 
                    placeholder={m.options_metadata_value_placeholder()} 
                    bind:value={metadataValue}
                    onkeydown={handleKeydown}
                    id="metadataValueInput"
                    aria-label={m.options_metadata_value_label()}
                >
                
                <button 
                    class="btn btn-primary" 
                    type="button" 
                    title={m.options_metadata_add_button()} 
                    onclick={addMetadata} 
                    id="addMetadataButton"
                    disabled={!metadataType || !metadataValue}
                >
                    <i class="bi bi-plus-lg"></i> {m.options_metadata_add_button()}
                </button>
            </div>
        </div>
    </div>

    {#if metadataState.length > 0}
        <div class="metadata-list-card">
            <div class="metadata-list-header">
                <h5>{m.options_metadata_list_title()}</h5>
            </div>
            <div class="metadata-list">
                {#each metadataState as metadata (metadata.id)}
                    <div class="metadata-item" transition:fade={{ duration: 150 }}>
                        <div class="metadata-content">
                            <div class="metadata-type">
                                <span class="label">{m.options_metadata_type_label_inline()}</span>
                                <span class="value">{metadata.type}</span>
                            </div>
                            <div class="metadata-value">
                                <span class="label">{m.options_metadata_value_label_inline()}</span>
                                <span class="value">{metadata.value}</span>
                            </div>
                        </div>
                        <button 
                            class="btn btn-sm btn-icon" 
                            type="button" 
                            title={m.options_metadata_delete_button()} 
                            onclick={() => removeMetadata(metadata.id)} 
                            id="deleteMetadataButton-{metadata.id}"
                            aria-label={m.options_metadata_remove_label()}
                        >
                            <i class="bi bi-trash"></i>
                        </button>
                    </div>
                {/each}
            </div>
        </div>
    {:else}
        <div class="empty-metadata">
            <div class="empty-icon">
                <i class="bi bi-info-circle"></i>
            </div>
            <p>{m.options_metadata_empty_state()}</p>
        </div>
    {/if}
</div>

<style>
    .metadata-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .add-metadata-card,
    .metadata-list-card {
        background-color: var(--bs-light);
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    }

    .add-metadata-header,
    .metadata-list-header {
        padding: 1rem;
        border-bottom: 1px solid rgba(0,0,0,0.1);
    }

    .add-metadata-header h5,
    .metadata-list-header h5 {
        margin-bottom: 0.25rem;
        font-weight: 600;
    }

    .add-metadata-form {
        padding: 1rem;
    }

    .metadata-list {
        max-height: 350px;
        overflow-y: auto;
    }

    .metadata-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1rem;
        border-bottom: 1px solid rgba(0,0,0,0.05);
        transition: background-color 0.15s ease;
    }

    .metadata-item:hover {
        background-color: rgba(0,0,0,0.02);
    }

    .metadata-content {
        display: flex;
        flex-grow: 1;
        gap: 1.5rem;
    }

    .metadata-type,
    .metadata-value {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .metadata-type {
        min-width: 200px;
    }

    .label {
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--bs-secondary);
    }

    .value {
        font-size: 1rem;
        word-break: break-word;
    }

    .btn-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        color: var(--bs-danger);
        background: transparent;
        transition: background-color 0.15s ease;
    }

    .btn-icon:hover {
        background-color: rgba(220, 53, 69, 0.1);
    }

    .empty-metadata {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        text-align: center;
        background-color: var(--bs-light);
        border-radius: 0.5rem;
        color: var(--bs-secondary);
    }

    .empty-icon {
        font-size: 2rem;
        margin-bottom: 1rem;
        opacity: 0.6;
    }
</style>
